import i18n from "@/i18n"

// Time helpers
export const MINUTE = 60
export const HOUR = 3600
export const DAY = 86400

// Option generators - these are functions to ensure i18n.t is called at render time
export const getMaxAdvanceOptions = () => {
  const MAX_ADVANCE_DAYS = 30
  return [
    { label: i18n.t("app.settings.venues.reservation_rules.options.none"), value: 0 },
    ...Array.from({ length: MAX_ADVANCE_DAYS }, (_, i) => {
      const days = i + 1
      return {
        label: i18n.t("app.settings.venues.reservation_rules.options.days", { count: days }),
        value: days * DAY,
      }
    }),
  ]
}

export const getMinAdvanceOptions = () => {
  const baseOptions = [
    { label: i18n.t("app.settings.venues.reservation_rules.options.none"), value: 0 },
    { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 15 }), value: 15 * MINUTE },
    { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 30 }), value: 30 * MINUTE },
    { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 45 }), value: 45 * MINUTE },
    { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 1 }), value: HOUR },
    { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 2 }), value: 2 * HOUR },
    { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 4 }), value: 4 * HOUR },
    { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 8 }), value: 8 * HOUR },
    { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 12 }), value: 12 * HOUR },
  ]

  const dayOptions = Array.from({ length: 7 }, (_, i) => {
    const days = i + 1
    return {
      label: i18n.t("app.settings.venues.reservation_rules.options.days", { count: days }),
      value: days * DAY,
    }
  })

  return [...baseOptions, ...dayOptions]
}

export const getMinReservationTimeOptions = () => [
  { label: i18n.t("app.settings.venues.reservation_rules.options.none"), value: 0 },
  { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 30 }), value: 30 * MINUTE },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 1 }), value: HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 2 }), value: 2 * HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 3 }), value: 3 * HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 4 }), value: 4 * HOUR },
]

export const getMaxReservationTimeOptions = () => {
  const options = [
    { label: i18n.t("app.settings.venues.reservation_rules.options.none"), value: 0 },
    { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 30 }), value: 30 * MINUTE },
    { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 45 }), value: 45 * MINUTE },
  ]

  for (let half = 1; half <= 24; half++) {
    const hours = half * 0.5
    const seconds = Math.round(hours * HOUR)
    const label =
      hours % 1 === 0
        ? i18n.t("app.settings.venues.reservation_rules.options.hours", { count: hours })
        : i18n.t("app.settings.venues.reservation_rules.options.hours_decimal", { count: hours })
    options.push({ label, value: seconds })
  }

  return options
}

export const getReservationBufferOptions = () => [
  { label: i18n.t("app.settings.venues.reservation_rules.options.none"), value: 0 },
  { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 15 }), value: 15 * MINUTE },
  { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 30 }), value: 30 * MINUTE },
  { label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: 45 }), value: 45 * MINUTE },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 1 }), value: HOUR },
  {
    label: i18n.t("app.settings.venues.reservation_rules.options.hours_and_minutes", { hours: 1, minutes: 30 }),
    value: HOUR + 30 * MINUTE,
  },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours", { count: 2 }), value: 2 * HOUR },
]

export const getNoShowOptions = () => [
  { label: i18n.t("app.settings.venues.reservation_rules.options.never"), value: null },
  ...[5, 10, 15, 20, 25, 30, 45, 60, 90, 120].map((m) => ({
    label: i18n.t("app.settings.venues.reservation_rules.options.minutes", { count: m }),
    value: m * MINUTE,
  })),
]

export const getFreeCancellationOptions = () => [
  { label: i18n.t("app.settings.venues.reservation_rules.options.minutes_before", { count: 30 }), value: 30 * MINUTE },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours_before", { count: 1 }), value: HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours_before", { count: 2 }), value: 2 * HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours_before", { count: 6 }), value: 6 * HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.days_before", { count: 1 }), value: DAY },
]

export const getCancellationDeadlineOptions = () => [
  { label: i18n.t("app.settings.venues.reservation_rules.options.none"), value: 0 },
  { label: i18n.t("app.settings.venues.reservation_rules.options.minutes_before", { count: 30 }), value: 30 * MINUTE },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours_before", { count: 1 }), value: HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours_before", { count: 2 }), value: 2 * HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.hours_before", { count: 6 }), value: 6 * HOUR },
  { label: i18n.t("app.settings.venues.reservation_rules.options.days_before", { count: 1 }), value: DAY },
]

export const getLargeGroupThresholds = () =>
  Array.from({ length: 9 }, (_, i) => {
    const n = 4 + i * 2 // 4,6,8,...20
    return {
      label: i18n.t("app.settings.venues.reservation_rules.options.large_group", { count: n }),
      value: n,
    }
  })

// Placeholder initial values (all time values are in seconds)
export const getPlaceholderInitialValues = () => ({
  maxAdvanceBooking: 0,
  minAdvanceBooking: 0,
  minReservationTime: 0,
  maxReservationTime: 0,
  autoCancelAfter: 30 * MINUTE,
  largeGroupThreshold: 8,
})
